import React, { useState } from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  PhoneIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { supabase } from './lib/supabase';

function AuthModal({ isOpen, onClose, onAuthSuccess }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    loginIdentifier: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');

  const isPhoneInput = (value) => /^\d+$/.test(value) && !value.includes('@');

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 10) {
        setFormData({ ...formData, [name]: digits });
      }
      return;
    }

    if (name === 'loginIdentifier') {
      if (isPhoneInput(value)) {
        const digits = value.replace(/\D/g, '');
        if (digits.length <= 10) {
          setFormData({ ...formData, [name]: digits });
        }
      } else {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }

    if (name === 'fullName') {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const lookupEmailByPhone = async (phone) => {
    const digits = String(phone).replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return null;
    const { data, error } = await supabase.rpc('get_email_by_phone', { p_phone: digits });
    if (error || data == null || data === '') return null;
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (isSignUp) {
      if (!formData.fullName.trim()) {
        setError('Full name is required');
        setLoading(false);
        return;
      }
      if (formData.phone.length !== 10) {
        setError('Phone number must be exactly 10 digits');
        setLoading(false);
        return;
      }
      if (!/^[6-9]/.test(formData.phone)) {
        setError('Please enter a valid Indian mobile number');
        setLoading(false);
        return;
      }
      if (!formData.email?.trim()) {
        setError('Email is required');
        setLoading(false);
        return;
      }
    }

    try {
      if (isSignUp) {
        const emailTrimmed = formData.email?.trim()?.toLowerCase();
        const { data: emailExists, error: emailCheckError } = await supabase.rpc('check_email_exists', {
          p_email: emailTrimmed
        });
        if (emailCheckError) throw emailCheckError;
        if (emailExists) {
          setError('An account with this email already exists. Please sign in or use a different email.');
          setLoading(false);
          return;
        }

        const phoneEmail = await lookupEmailByPhone(formData.phone);
        if (phoneEmail) {
          setError('An account with this phone number already exists. Please sign in or use a different number.');
          setLoading(false);
          return;
        }

        const { data, error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.fullName,
              phone: formData.phone
            }
          }
        });

        if (error) throw error;

        if (data.user) {
          setSignUpEmail(formData.email);
          setSignUpSuccess(true);
          setError('');
        }
      } else {
        let signInEmail = formData.loginIdentifier;

        if (isPhoneInput(formData.loginIdentifier)) {
          if (formData.loginIdentifier.length !== 10) {
            setError('Phone number must be exactly 10 digits');
            setLoading(false);
            return;
          }
          const email = await lookupEmailByPhone(formData.loginIdentifier);
          if (!email) {
            setError('No account found with this phone number');
            setLoading(false);
            return;
          }
          signInEmail = email;
        }

        const { data, error } = await supabase.auth.signInWithPassword({
          email: signInEmail,
          password: formData.password
        });

        if (error) throw error;

        if (data.user) {
          onAuthSuccess(data.user);
          resetForm();
          onClose();
        }
      }
    } catch (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!resetEmail.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const { data: exists, error: checkError } = await supabase.rpc('check_email_exists', {
        p_email: resetEmail.trim()
      });

      if (checkError) throw checkError;
      if (!exists) {
        setError('No account found with this email address.');
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setResetSent(true);
    } catch (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', fullName: '', phone: '', loginIdentifier: '' });
    setError('');
    setShowPassword(false);
    setIsForgotPassword(false);
    setResetEmail('');
    setResetSent(false);
    setSignUpSuccess(false);
    setSignUpEmail('');
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-smooth-rise">

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 px-6 py-8 text-center relative">
          <button
            onClick={() => { resetForm(); onClose(); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">{isForgotPassword ? '🔑' : '🏸'}</span>
          </div>
          <h2 className="text-2xl font-bold text-white">
            {signUpSuccess ? 'Check Your Email' : isForgotPassword ? 'Reset Password' : isSignUp ? 'Create Account' : 'Welcome Back'}
          </h2>
          <p className="text-green-100 text-sm mt-1">
            {signUpSuccess
              ? 'Confirm your email and come back to login'
              : isForgotPassword
              ? 'We\'ll send you a reset link'
              : isSignUp ? 'Join Best Badminton Club today' : 'Sign in with email or phone'}
          </p>
        </div>

        {/* Forgot Password View */}
        {isForgotPassword ? (
          <div className="p-6 space-y-4">
            {resetSent ? (
              <div className="text-center py-4">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <EnvelopeIcon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Check Your Email</h3>
                <p className="text-sm text-gray-600 mb-4">
                  We've sent a password reset link to <span className="font-semibold text-gray-800">{resetEmail}</span>
                </p>
                <p className="text-xs text-gray-500 mb-6">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <button
                  onClick={() => { setIsForgotPassword(false); setResetSent(false); setResetEmail(''); setError(''); }}
                  className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-green-600 transition-all duration-200 shadow-md"
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      required
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow text-sm"
                    />
                  </div>
                </div>

                {error && (
                  <div className="text-sm px-4 py-3 rounded-lg flex items-start gap-2 bg-red-50 text-red-700 border border-red-200">
                    <span>!</span>
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Sending...</span>
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => { setIsForgotPassword(false); setError(''); }}
                  className="w-full text-sm text-gray-500 hover:text-green-600 transition-colors py-2"
                >
                  Back to Sign In
                </button>
              </form>
            )}
          </div>
        ) : signUpSuccess ? (
          <div className="p-6 space-y-4">
            <div className="text-center py-4">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <EnvelopeIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verification Email Sent</h3>
              <p className="text-sm text-gray-600 mb-2">
                A verification link has been sent to <span className="font-semibold text-gray-800">{signUpEmail}</span>.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Please confirm your email using the link and then come back to login.
              </p>
              <p className="text-xs text-gray-500 mb-6">
                Didn&apos;t receive the email? Check your spam folder.
              </p>
              <button
                onClick={() => { setSignUpSuccess(false); setSignUpEmail(''); setIsSignUp(false); setError(''); }}
                className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-green-600 transition-all duration-200 shadow-md"
              >
                Back to Login
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Tab Switcher */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => { if (isSignUp) toggleMode(); }}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                  !isSignUp
                    ? 'text-green-600 border-b-2 border-green-600 bg-green-50/50'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => { if (!isSignUp) toggleMode(); }}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                  isSignUp
                    ? 'text-green-600 border-b-2 border-green-600 bg-green-50/50'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                Sign Up
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {isSignUp && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        required
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="10 digit mobile number"
                        maxLength="10"
                        required
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow text-sm"
                      />
                    </div>
                  </div>
                </>
              )}

              {isSignUp ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@example.com"
                      required
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email or Phone Number</label>
                  <div className="relative">
                    {isPhoneInput(formData.loginIdentifier) ? (
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    ) : (
                      <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    )}
                    <input
                      type="text"
                      name="loginIdentifier"
                      value={formData.loginIdentifier}
                      onChange={handleInputChange}
                      placeholder="Email or 10-digit phone number"
                      required
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow text-sm"
                    />
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  {!isSignUp && (
                    <button
                      type="button"
                      onClick={() => { setIsForgotPassword(true); setError(''); setResetEmail(isPhoneInput(formData.loginIdentifier) ? '' : formData.loginIdentifier); }}
                      className="text-xs text-green-600 hover:text-green-700 font-medium transition-colors"
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={isSignUp ? 'Min 6 characters' : 'Enter your password'}
                    required
                    minLength={6}
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className={`text-sm px-4 py-3 rounded-lg flex items-start gap-2 ${
                  error.includes('verification')
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  <span>{error.includes('verification') ? '✓' : '!'}</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Please wait...</span>
                  </>
                ) : (
                  isSignUp ? 'Create Account' : 'Sign In'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthModal;